import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Search,
  Mail,
  MessageCircle,
  Shield,
  Wallet,
  Trophy,
  Users,
  Gamepad2,
  Download,
  Coins,
  Award,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const FAQ = () => {
  const [expandedFaqs, setExpandedFaqs] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const toggleFaq = (id: string) => {
    if (expandedFaqs.includes(id)) {
      setExpandedFaqs(expandedFaqs.filter(faqId => faqId !== id));
    } else {
      setExpandedFaqs([...expandedFaqs, id]);
    }
  };

  const categories = [
    { id: 'all', name: 'All Questions', icon: <HelpCircle className="w-4 h-4" /> },
    { id: 'account', name: 'Account', icon: <Users className="w-4 h-4" /> },
    { id: 'tournaments', name: 'Tournaments', icon: <Trophy className="w-4 h-4" /> },
    { id: 'payments', name: 'Payments', icon: <Wallet className="w-4 h-4" /> },
    { id: 'withdrawals', name: 'Withdrawals', icon: <Coins className="w-4 h-4" /> },
    { id: 'fairplay', name: 'Fair Play', icon: <Shield className="w-4 h-4" /> },
    { id: 'technical', name: 'Technical', icon: <Gamepad2 className="w-4 h-4" /> },
    { id: 'download', name: 'Download', icon: <Download className="w-4 h-4" /> },
  ];

  const faqs = [
    {
      id: 'faq1',
      category: 'account',
      question: 'How do I create an account on TheArenaX?',
      answer: 'Creating an account is simple! Download the app, click on "Sign Up", and enter your email or phone number. You\'ll receive an OTP for verification. Once verified, you can complete your profile with your in-game IDs for BGMI and Free Fire.'
    },
    {
      id: 'faq2',
      category: 'account',
      question: 'Is there an age restriction?',
      answer: 'Yes, you must be 18 years or older to use TheArenaX. We verify age during registration and may request KYC documents for withdrawals above certain limits.'
    },
    {
      id: 'faq3',
      category: 'account',
      question: 'Can I have multiple accounts?',
      answer: 'No, creating multiple accounts is strictly prohibited and violates our Fair Play Policy. Users found with multiple accounts will be permanently banned and all winnings will be forfeited.'
    },
    {
      id: 'faq4',
      category: 'account',
      question: 'How do I delete my account?',
      answer: 'Go to Profile → Account Settings → Delete Account. This action is permanent and will remove all your data, including balance and match history. Make sure to withdraw any remaining balance first.'
    },
    {
      id: 'faq5',
      category: 'tournaments',
      question: 'How do I join a tournament?',
      answer: 'Go to the Tournaments section, select BGMI or Free Fire, choose a tournament that matches your skill tier, and tap "Join Match". You\'ll need sufficient coins in your wallet for the entry fee. Fill in your in-game ID and confirm your entry.'
    },
    {
      id: 'faq6',
      category: 'tournaments',
      question: 'What are the entry fees?',
      answer: 'Entry fees vary by tournament tier and prize pool. T3 tournaments start from as low as 25 coins, while S-tier championships may require more. All entry fees are clearly displayed on each tournament card.'
    },
    {
      id: 'faq7',
      category: 'tournaments',
      question: 'Can I create my own team?',
      answer: 'Yes! For squad tournaments, you can create a team. Set a team name and password, then share the Team ID with your friends. Team creators can manage their roster and view team details.'
    },
    {
      id: 'faq8',
      category: 'tournaments',
      question: 'How do I join an existing team?',
      answer: 'Ask your team creator for the Team ID and password. Go to the tournament, select "Join Existing Team", enter the credentials and your in-game details. You\'ll be added to the team roster.'
    },
    {
      id: 'faq9',
      category: 'payments',
      question: 'How do I add coins to my wallet?',
      answer: 'Go to Wallet → Add Coins, select a coin package, choose your payment method (UPI, Card, Paytm), and complete the payment. Coins will be credited instantly to your wallet.'
    },
    {
      id: 'faq10',
      category: 'payments',
      question: 'Are coin purchases refundable?',
      answer: 'Coin purchases are final and non-refundable as per our Refund Policy. Please ensure you want to purchase before confirming. In case of failed transactions, contact support with payment proof.'
    },
    {
      id: 'faq11',
      category: 'payments',
      question: 'What payment methods are accepted?',
      answer: 'We accept UPI, Credit/Debit Cards, and Paytm. All payments are processed securely through our payment partners.'
    },
    {
      id: 'faq12',
      category: 'withdrawals',
      question: 'How do I withdraw my winnings?',
      answer: 'Go to Wallet → Redeem, enter your UPI ID and the amount you wish to withdraw (minimum 25 coins). Submit the request and our team will process it within 24-48 hours.'
    },
    {
      id: 'faq13',
      category: 'withdrawals',
      question: 'What is the minimum withdrawal amount?',
      answer: 'The minimum withdrawal is 25 coins (₹25). There\'s no maximum limit, but higher amounts may require additional verification for security purposes.'
    },
    {
      id: 'faq14',
      category: 'withdrawals',
      question: 'How long do withdrawals take?',
      answer: 'Withdrawals are processed within 24-48 hours after verification. Once processed, the amount will be credited to your UPI account instantly or within a few hours depending on your bank.'
    },
    {
      id: 'faq15',
      category: 'withdrawals',
      question: 'Is there any tax on winnings?',
      answer: 'Yes, as per Indian tax laws, TDS of 30% is applicable on net winnings exceeding ₹10,000 in a financial year. PAN card is required for withdrawals above this limit.'
    },
    {
      id: 'faq16',
      category: 'fairplay',
      question: 'How do you prevent cheating?',
      answer: 'We use multiple anti-cheat systems including behavior analysis, manual review of suspicious activities, and screen recording verification. Players found cheating face permanent bans.'
    },
    {
      id: 'faq17',
      category: 'fairplay',
      question: 'What happens if I report someone?',
      answer: 'Your report is reviewed by our moderation team within 24 hours. If evidence is found, appropriate action is taken against the offender. All reports are kept confidential.'
    },
    {
      id: 'faq18',
      category: 'fairplay',
      question: 'Is TheArenaX legal?',
      answer: 'Absolutely! TheArenaX hosts skill-based tournaments compliant with Indian gaming laws. Our games are recognized as games of skill by the Supreme Court of India. Entry fees are for participation, not betting.'
    },
    {
      id: 'faq19',
      category: 'technical',
      question: 'What should I do if the app crashes?',
      answer: 'First, try restarting the app. If the issue persists, clear app cache or reinstall. Make sure your device meets the minimum requirements (Android 8.0+, 3GB RAM). Contact support if problems continue.'
    },
    {
      id: 'faq20',
      category: 'technical',
      question: 'Why am I not receiving OTP?',
      answer: 'Check if you have network connectivity. Ensure you\'ve entered the correct mobile number. Wait 2 minutes and try resending. If still not received, check your SMS spam folder or try with a different number.'
    },
    {
      id: 'faq21',
      category: 'download',
      question: 'How do I download the app?',
      answer: 'Visit our Download page and click the "Download APK" button. After download, open the file and allow installation from unknown sources if prompted. Follow the on-screen instructions to complete installation.'
    },
    {
      id: 'faq22',
      category: 'download',
      question: 'Is the app available on iOS?',
      answer: 'The iOS version is currently under development and will be available soon. Android users can download the APK directly from our website.'
    },
    {
      id: 'faq23',
      category: 'download',
      question: 'Is it safe to install the APK?',
      answer: 'Yes, our APK is verified and scanned for malware. Always download only from our official website to ensure you get the authentic, safe version of the app.'
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const stats = [
    { value: '24/7', label: 'Support Available', icon: Clock },
    { value: '< 24h', label: 'Response Time', icon: CheckCircle },
    { value: '95%', label: 'Satisfaction Rate', icon: Award },
    { value: '10K+', label: 'Questions Answered', icon: MessageCircle },
  ];

    const handleDownload = () => {
  // Create a temporary anchor element
  const link = document.createElement('a');
  
  // Path to your APK file (place it in public/apk/ folder)
  link.href = '/apk/TheArenaX-v2.0.0.apk';
  
  // Suggested filename for download
  link.download = 'TheArenaX-v2.0.0.apk';
  
  // Append to body, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  return (
    <div className="min-h-screen bg-[#0A0F1E]">
      <Navbar />
      
      {/* Hero Section with Animated Background */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-[#00fff5] rounded-full filter blur-3xl opacity-10"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#7000ff] rounded-full filter blur-3xl opacity-10"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-block p-4 bg-gradient-to-r from-[#00fff5] to-[#7000ff] rounded-2xl mb-6">
              <HelpCircle className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-5xl md:text-7xl font-rajdhani font-bold text-white mb-6">
              Frequently Asked <span className="text-[#00fff5]">Questions</span>
            </h1>
            <p className="text-xl text-gray-400 font-rajdhani max-w-3xl mx-auto mb-8">
              Find answers to common questions about TheArenaX, tournaments, payments, withdrawals, and more.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search your question..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#1E2338] border border-[#2A2F45] rounded-xl py-4 pl-12 pr-4 text-white font-rajdhani placeholder-gray-500 focus:border-[#00fff5] focus:outline-none transition"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-2xl p-6 text-center group hover:border-[#00fff5] transition"
                >
                  <div className="inline-block p-3 bg-[#00fff510] rounded-xl mb-3 group-hover:bg-[#00fff520] transition">
                    <IconComponent className="w-6 h-6 text-[#00fff5]" />
                  </div>
                  <div className="text-2xl md:text-3xl font-rajdhani font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-400 font-rajdhani">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl font-rajdhani font-bold text-sm transition ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-[#00fff5] to-[#7000ff] text-white'
                    : 'bg-[#1E2338] border border-[#2A2F45] text-gray-400 hover:border-[#00fff5] hover:text-white'
                }`}
              >
                {category.icon}
                {category.name}
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {filteredFaqs.length > 0 ? (
            <div className="space-y-4">
              {filteredFaqs.map((faq) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="bg-[#1E2338] border border-[#2A2F45] rounded-xl overflow-hidden hover:border-[#00fff5] transition"
                >
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className="w-full p-6 flex items-center justify-between text-left hover:bg-[#2A2F45] transition"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-rajdhani font-bold px-2 py-1 rounded-full bg-[#00fff510] text-[#00fff5] uppercase">
                          {categories.find(c => c.id === faq.category)?.name}
                        </span>
                      </div>
                      <span className="text-white font-rajdhani font-bold text-lg">{faq.question}</span>
                    </div>
                    {expandedFaqs.includes(faq.id) ? (
                      <ChevronUp className="w-5 h-5 text-[#00fff5] flex-shrink-0 ml-4" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" />
                    )}
                  </button>
                  
                  {expandedFaqs.includes(faq.id) && (
                    <div className="px-6 pb-6">
                      <div className="h-px bg-[#2A2F45] mb-4"></div>
                      <p className="text-gray-400 font-rajdhani leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#1E2338] border border-[#2A2F45] rounded-2xl p-12 text-center"
            >
              <AlertCircle className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-2xl font-rajdhani font-bold text-white mb-2">No questions found</h3>
              <p className="text-gray-400 mb-6">Try a different search term or category</p>
              <button
                onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
                className="bg-gradient-to-r from-[#00fff5] to-[#7000ff] px-6 py-3 rounded-xl text-white font-rajdhani font-bold hover:opacity-90 transition"
              >
                Clear Filters
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-[#00fff510] to-[#7000ff10] border border-[#2A2F45] rounded-3xl p-12 text-center"
          >
            <div className="inline-block p-4 bg-gradient-to-r from-[#00fff5] to-[#7000ff] rounded-2xl mb-6">
              <MessageCircle className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              Still Have <span className="text-[#00fff5]">Questions</span>?
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani mb-8 max-w-2xl mx-auto">
              Can't find the answer you're looking for? Our support team is here to help 24/7.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a 
                href="mailto:support@lastzoneindia.com"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00fff5] to-[#7000ff] px-8 py-4 rounded-xl text-white font-rajdhani font-bold hover:opacity-90 transition"
              >
                <Mail className="w-5 h-5" />
                support@lastzoneindia.com
              </a>
              
              <Link to="/download">
                <button onClick={handleDownload} className="bg-[#1E2338] border border-[#2A2F45] px-8 py-4 rounded-xl text-white font-rajdhani font-bold hover:bg-[#2A2F45] transition flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Download App
                </button>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              <span>Average response time: 2-4 hours</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/terms">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-[#1E2338] border border-[#2A2F45] rounded-xl p-4 text-center hover:border-[#00fff5] transition cursor-pointer"
              >
                <span className="text-white font-rajdhani text-sm">Terms & Conditions</span>
              </motion.div>
            </Link>
            <Link to="/privacy">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-[#1E2338] border border-[#2A2F45] rounded-xl p-4 text-center hover:border-[#00fff5] transition cursor-pointer"
              >
                <span className="text-white font-rajdhani text-sm">Privacy Policy</span>
              </motion.div>
            </Link>
            <Link to="/refund">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-[#1E2338] border border-[#2A2F45] rounded-xl p-4 text-center hover:border-[#00fff5] transition cursor-pointer"
              >
                <span className="text-white font-rajdhani text-sm">Refund Policy</span>
              </motion.div>
            </Link>
            <Link to="/fairplay">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-[#1E2338] border border-[#2A2F45] rounded-xl p-4 text-center hover:border-[#00fff5] transition cursor-pointer"
              >
                <span className="text-white font-rajdhani text-sm">Fair Play</span>
              </motion.div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;